"use client";

/**
 * Daily free-download counter shown under the Download button. Escalates:
 * subtle counter → near-limit warning → at-limit block. Shared by
 * AssetPurchaseCard (desktop rail) and MobilePurchaseBar so mobile users get
 * the same pre-emptive warning instead of a surprise 429.
 *
 * Two ways out are always offered, cheapest first: link Discord for +3/day
 * (free, instant, and the reason most people join the server), then the Buyer
 * Plus trial for unlimited.
 */

import Link from "next/link";
import AlertTriangle from "lucide-react/dist/esm/icons/alert-triangle";
import { type DailyDownloadStatus } from "@/hooks/useDailyDownloadCount";
import { DISCORD_DOWNLOAD_BONUS } from "@/lib/discord-perks";

interface DailyDownloadBadgeProps {
  daily: DailyDownloadStatus & { isLoading: boolean };
  /** Single-line rendering for the tight mobile purchase bar. */
  compact?: boolean;
}

export function DailyDownloadBadge({ daily, compact }: DailyDownloadBadgeProps) {
  if (!daily.isLimited || daily.isLoading) return null;

  const { used, limit, remaining, isAtLimit, isNearLimit, discordBonusAvailable } = daily;

  if (compact) {
    if (used === 0) return null;
    return (
      <p className={`text-2xs text-center ${isAtLimit ? "text-destructive" : isNearLimit ? "text-warning" : "text-muted-foreground"}`}>
        {isAtLimit ? `Daily limit reached (${limit}/${limit})` : `${used}/${limit} daily downloads used`}
        {" · "}
        {discordBonusAvailable ? (
          <Link href="/dashboard/discord" className="font-semibold text-discord underline underline-offset-2">
            +{DISCORD_DOWNLOAD_BONUS} with Discord
          </Link>
        ) : (
          <Link href="/pricing" className="font-semibold text-primary underline underline-offset-2">
            {isAtLimit || isNearLimit ? "Start free trial" : "Go unlimited"}
          </Link>
        )}
      </p>
    );
  }

  if (isAtLimit) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 space-y-1.5">
        <div className="flex items-center gap-2 text-xs font-semibold text-destructive">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          Daily download limit reached ({limit}/{limit})
        </div>
        <p className="text-xs text-muted-foreground">
          Free accounts can download {limit} assets per day. Resets at midnight UTC.
        </p>
        {discordBonusAvailable && (
          <Link
            href="/dashboard/discord"
            className="inline-flex items-center gap-1 text-xs font-semibold text-discord hover:underline"
          >
            Link Discord → +{DISCORD_DOWNLOAD_BONUS} downloads a day, free →
          </Link>
        )}
        <Link
          href="/pricing"
          className="block text-xs font-semibold text-primary hover:underline"
        >
          Or start a free trial — unlimited downloads →
        </Link>
      </div>
    );
  }

  if (isNearLimit) {
    return (
      <div className="rounded-lg border border-warning/30 bg-warning/5 px-3 py-2 space-y-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-warning">
            <AlertTriangle className="h-3 w-3 shrink-0" />
            {remaining} download{remaining === 1 ? "" : "s"} left today
          </div>
          <span className="text-xs text-muted-foreground">{used}/{limit}</span>
        </div>
        <div className="h-1 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full bg-warning rounded-full transition-all"
            style={{ width: `${(used / limit) * 100}%` }}
          />
        </div>
        {discordBonusAvailable ? (
          <Link
            href="/dashboard/discord"
            className="inline-flex items-center gap-1 text-xs font-semibold text-discord hover:underline"
          >
            Link Discord for +{DISCORD_DOWNLOAD_BONUS} a day — free →
          </Link>
        ) : (
          <Link
            href="/pricing"
            className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
          >
            Start your free trial — unlimited downloads →
          </Link>
        )}
      </div>
    );
  }

  // Show subtle counter once they've used any downloads
  if (used > 0) {
    return (
      <p className="text-xs text-muted-foreground text-center">
        {used}/{limit} daily downloads used
        {" · "}
        {discordBonusAvailable ? (
          <Link href="/dashboard/discord" className="text-discord hover:underline underline-offset-2">
            +{DISCORD_DOWNLOAD_BONUS} with Discord
          </Link>
        ) : (
          <Link href="/pricing" className="hover:text-foreground underline underline-offset-2">
            Go unlimited
          </Link>
        )}
      </p>
    );
  }

  return null;
}
